import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import DetailYoutubeEmbed from './detail-youtube-embed'
import useDetailStyles from '../detail-styles'
import DetailSection from './detail-section'
import DownloadPlanHeaderAction from './download-plan-header-action'
import { downloadLessonPlan } from '../lib/plan-download'
import useRegisterActivityPlanDetailHeader from '../hooks/use-register-activity-plan-detail-header'
import { getActivityVideoMediaId } from '../lib/resolve-youtube-media-id'

const ActivityBlock = ({ activity }) => {
  const classes = useDetailStyles()
  const activityVideoId = getActivityVideoMediaId(activity)

  if (!activity) {
    return null
  }

  return (
    <Box className={classes.activityBlock}>
      <Typography component="h3" className={classes.activityTitle}>
        {activity.title}
      </Typography>
      {activity.details?.summary ? (
        <Typography component="p" className={classes.bodyText}>
          {activity.details.summary}
        </Typography>
      ) : null}
      {activityVideoId ? (
        <Box className={classes.videoWrapper}>
          <DetailYoutubeEmbed embedId={activityVideoId} />
        </Box>
      ) : null}
    </Box>
  )
}

ActivityBlock.propTypes = {
  activity: PropTypes.object,
}

const LessonPlanDetailView = ({
  planHit,
  activityHits,
  inlineActions = false,
}) => {
  const classes = useDetailStyles()
  const activityHitsById = activityHits.reduce((map, hit) => {
    map[hit.objectID] = hit
    return map
  }, {})

  const subtitle =
    planHit.totalDurationMins != null
      ? `${planHit.totalDurationMins} minutes total`
      : ''

  const {
    HeaderActionsPortal,
    renderActionsInline,
  } = useRegisterActivityPlanDetailHeader({
    title: planHit.title,
    subtitle,
    inlineActions,
  })

  const downloadAction = (
    <DownloadPlanHeaderAction
      buttonLabel="Download Plan"
      dialogTitle="Download plan"
      dialogDescription="Download this lesson plan as a text file. Click Download to save it to your device."
      onDownload={() => downloadLessonPlan(planHit, activityHits)}
    />
  )

  return (
    <>
      {!renderActionsInline ? (
        <HeaderActionsPortal>{downloadAction}</HeaderActionsPortal>
      ) : null}
      <Box className={classes.contentContainer}>
        {renderActionsInline ? (
          <Box className={classes.header}>
            <Box className={classes.headerCopy}>
              <Typography component="h1" className={classes.title}>
                {planHit.title}
              </Typography>
              {subtitle ? (
                <Typography component="p" className={classes.meta}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            {downloadAction}
          </Box>
        ) : null}
        {(planHit.sections || []).map(section => (
          <Box key={section.name} className={classes.planSection}>
            <Typography component="h2" className={classes.planSectionTitle}>
              {section.name}
            </Typography>
            <DetailSection>
              {(section.activities || []).map(activityId => (
                <ActivityBlock
                  key={activityId}
                  activity={activityHitsById[activityId]}
                />
              ))}
            </DetailSection>
          </Box>
        ))}
      </Box>
    </>
  )
}

LessonPlanDetailView.propTypes = {
  planHit: PropTypes.object.isRequired,
  activityHits: PropTypes.arrayOf(PropTypes.object).isRequired,
  inlineActions: PropTypes.bool,
}

export default LessonPlanDetailView
